(ns nosco.richtext.ai
  (:require
   ["prosemirror-state" :as pm-state]
   ["prosemirror-view" :as pm-view]
   ["react" :as React]
   ["react-useportal" :default usePortal]
   ["sse.js" :as sse]
   [cljs-bean.core :as bean]
   [clojure.string :as string]
   [goog.object :as gobj]
   [hx.hooks :as hooks]
   [hx.react :refer [defnc]]
   [nosco.colors :as colors]
   [nosco.controllers :as controllers]
   [nosco.i18n.translate :as t]
   [nosco.icons2 :as icons2]
   [nosco.richtext.api :as richtext-api]
   [nosco.richtext.markdown :as richtext-markdown]
   [nosco.richtext.prosemirror :as pm]
   [nosco.router :as router]
   [nosco.ui.pills :as pills]
   [nosco.ui.popup :as popup]
   [nosco.ui.toast :as toast]
   [shadow.resource :as shadow-resource]))

(defn get-model-stream [url payload on-delta]
  (js/Promise.
   (fn [resolve reject]
     (let [opts {:method "POST"
                 :headers {"Content-Type" "application/json"}
                 :payload (.stringify js/JSON (bean/->js payload))}
           sse (sse/SSE. url (bean/->js opts))
           response (volatile! "")]

       (doto sse
         (.addEventListener
          "delta"
          (fn [event]
            (vswap! response str (.-data event))
            (on-delta (.-data event))))

         (.addEventListener
          "end"
          (fn [_event]
            (.close sse)
            (resolve @response)))

         (.addEventListener
          "exception"
          (fn [_event]
            (.close sse)
            (reject)))

         (.stream))))))

(defn make-text-message [role text]
  {:role (name role)
   :content [{:type "text" :text text}]})

(defn ai-state-reducer [state action]
  (when ^boolean goog/DEBUG
    (js/console.debug "ai-state-reducer" (pr-str action)))

  (case (::type action)
    ::update-convert-top
    (assoc state ::convert-top (::convert-top action))

    ::open
    (let [convert-top (::convert-top state)]
      (when-not (::ideabox-id (::ai-context action))
        (throw (ex-info "Missing ideabox ID" {:action action})))
      (assoc state
             ::open true
             ::position (update (::position action)
                                :top convert-top)
             ::text (::text action)
             ::mode (if (::text action)
                      ::edit-mode
                      ::create-mode)
             ::ideabox-id (::ideabox-id (::ai-context action))
             ::idea-id (::idea-id (::ai-context action))
             ::template-field-id (::template-field-id (::ai-context action))
             ::field (::field (::ai-context action))))

    ::close
    (select-keys state [::idea-id ::template-field-id ::convert-top])

    ::initial-prompt-submitted
    (assoc state ::initial-prompt (::prompt action))

    ::follow-up-prompt-submitted
    (update state ::messages (fnil conj []) (make-text-message :user (::prompt action)))

    ::stream-started
    (assoc state
           ::loading true
           ::streaming-response nil)

    ::stream-ended
    (-> state
        (assoc ::loading false
               ::streaming-response nil
               ::response (::response action))
        (update ::messages (fnil conj []) (make-text-message :assistant (::response action))))

    ::stream-error
    (assoc state
           ::loading false
           ::streaming-response nil)

    ::stream-delta-received
    (update state ::streaming-response str (::delta action))

    ::position-changed
    (assoc state
           ::position (::position action)
           ::text (::text action))))

(defn use-ai-state []
  (hooks/useReducer ai-state-reducer nil))

(defn open-ai-suggestions-action [ai-context width top left text]
  {::type ::open
   ::ai-context ai-context
   ::position {:width width :top top :left left}
   ::text text})

(defn close-ai-suggestions-action []
  {::type ::close})

(defn ai-suggestions-open? [state]
  (boolean (::open state)))

(defn reset-position! [ai-state dispatch editor-api]
  (let [state (richtext-api/get-state editor-api)
        view (richtext-api/get-view editor-api)
        selection (.-selection state)
        convert-top (::convert-top ai-state)
        selection-coords (.. ^js view (coordsAtPos (.-to selection)))]
    (dispatch {::type ::position-changed
               ::text (pm/selected-text state)
               ::position (assoc (::position ai-state)
                                 :top (convert-top (.-top selection-coords)))})))

(defn get-endpoint-url [state]
  (router/href :nosco.routes/ai-idea
               {:ideabox-id (::ideabox-id state)}
               {:ideaId (::idea-id state)
                :templateFieldId (::template-field-id state)}))

(defn show-error-toast! []
  (toast/show-message-error
   {:title (t/t "Error in Nosco AI response")
    :text (t/t "Please try again and contact support if the error persists")}))

(defn stream-response! [_state dispatch url payload on-delta]
  (-> (get-model-stream url payload on-delta)
      (.then
       (fn [response]
         (dispatch {::type ::stream-ended ::response response})))
      (.catch
       (fn []
         (show-error-toast!)
         (dispatch {::type ::stream-error}))))

  (dispatch {::type ::stream-started}))

(defn replace-selection-with-markdown!
  "Parses s as Markdown and replaces the current editor selection with its contents.
  Selects the inserted content."
  [editor-api s]
  (let [initial-state (richtext-api/get-state editor-api)
        initial-selection (.-selection initial-state)
        from (.-from initial-selection)
        to (.-to initial-selection)

        slice (richtext-markdown/markdown->slice (.-schema initial-state) s)

        replace-tr (pm/replace-selection-tr initial-state slice)

        ;; Use the transaction's mapping to find where the insertion ends
        ;; Map the end position through the replacement to get the actual end position
        new-to (.map (.-mapping replace-tr) to)

        _ (richtext-api/dispatch editor-api replace-tr)

        ;; Create a new selection from the original start to the mapped end position
        new-state (richtext-api/get-state editor-api)
        new-selection (pm/->text-selection (.-doc new-state) from new-to)

        _ (richtext-api/dispatch editor-api (pm/set-selection-tr new-state new-selection))]))

(defn stream-create-response!
  "Streams the AI response in create mode, by inserting the accumulated suggestion into the editor."
  [state dispatch editor-api url payload]
  (let [streaming-response (volatile! "")

        on-delta
        (fn [delta]
          ;; Parses the acculumated response and updates the editor, expanding the selection each time
          (let [streaming-response' (vswap! streaming-response str delta)]
            (replace-selection-with-markdown! editor-api streaming-response')
            (reset-position! state dispatch editor-api)))]

    (stream-response! state dispatch url payload on-delta)))

(defn submit-initial-create-prompt! [state dispatch editor-api prompt]
  (let [url (get-endpoint-url state)
        payload {:prompt prompt}]
    (dispatch {::type ::initial-prompt-submitted ::prompt prompt})
    (stream-create-response! state dispatch editor-api url payload)))

(defn submit-follow-up-create-prompt! [state dispatch editor-api prompt]
  (let [url (get-endpoint-url state)
        user-message (make-text-message :user prompt)
        payload {:prompt (::initial-prompt state)
                 :messages (into (::messages state) [user-message])}]
    (dispatch {::type ::follow-up-prompt-submitted ::prompt prompt})
    (stream-create-response! state dispatch editor-api url payload)))

(defn stream-edit-response! [state dispatch url payload]
  (let [on-delta
        (fn [delta]
          (dispatch {::type ::stream-delta-received ::delta delta}))]
    (stream-response! state dispatch url payload on-delta)))

(defn submit-initial-edit-prompt! [state dispatch editor-api prompt]
  (let [url (get-endpoint-url state)
        selected-text (richtext-markdown/selected-markdown (richtext-api/get-state editor-api))
        payload {:prompt prompt
                 :text selected-text}]
    (dispatch {::type ::initial-prompt-submitted ::prompt prompt})
    (stream-edit-response! state dispatch url payload)))

(defn submit-follow-up-edit-prompt! [state dispatch editor-api prompt]
  (let [url (get-endpoint-url state)
        selected-text (richtext-markdown/selected-markdown (richtext-api/get-state editor-api))
        user-message (make-text-message :user prompt)
        payload {:prompt (::initial-prompt state)
                 :text selected-text
                 :messages (into (::messages state) [user-message])}]
    (dispatch {::type ::follow-up-prompt-submitted ::prompt prompt})
    (stream-edit-response! state dispatch url payload)))

(defn close-overlay! [_state dispatch editor-api]
  (dispatch (close-ai-suggestions-action))
  (.focus (richtext-api/get-view editor-api)))

(defn copy-response-to-clipboard! [state _dispatch _editor-api]
  (let [text-blob (js/Blob. #js [(::response state)] #js {:type "text/plain"})
        html-blob (js/Blob. #js [(richtext-markdown/markdown->html (::response state))] #js {:type "text/html"})
        clipboard-item (js/ClipboardItem. #js {"text/plain" text-blob
                                               "text/html" html-blob})]
    (-> (.. js/navigator -clipboard (write #js [clipboard-item]))
        (.then
         (fn []
           (toast/show-confirmation {:title (t/t "Copied to clipboard")}))))))

(defn insert-response-below! [state _dispatch editor-api]
  (let [pm-state (richtext-api/get-state editor-api)

        pos (.. pm-state -selection -$to (after))

        slice (richtext-markdown/markdown->slice (.-schema pm-state)
                                                 ;; Insert empty <p>s for spacing above and below
                                                 (str "\n\n" (::response state) "\n\n"))

        _ (richtext-api/dispatch editor-api (pm/insert-slice-tr pm-state pos slice))

        new-pm-state (richtext-api/get-state editor-api)
        new-selection (pm/->text-selection (.-doc new-pm-state) pos (+ pos (.-size slice) 1))

        _ (richtext-api/dispatch editor-api (pm/set-selection-tr new-pm-state new-selection))]))

(def continue-writing-prompt
  "Extend the text without altering it")

(defnc ResponseMenu [{:keys [state dispatch editor-api]} ref]
  {:wrap [(React/forwardRef)]}
  [:div {:ref ref :class "nosco-richtext-ai-menu"}
   [:ul {:class "nosco-richtext-ai-menu__section"}
    (case (::mode state)
      ::create-mode
      [:<>
       [:li
        [:button
         {:type "button"
          :class "nosco-richtext-ai-menu__button"
          :on-click #(dispatch (close-ai-suggestions-action))}
         [icons2/Icon {:icon icons2/Done-small}]
         (t/t "Done")]]
       [:li
        [:button
         {:type "button"
          :class "nosco-richtext-ai-menu__button"
          :on-click #(submit-follow-up-create-prompt! state dispatch editor-api continue-writing-prompt)}
         [icons2/Icon {:icon icons2/Create-small}]
         (t/t "Continue writing")]]]

      ::edit-mode
      [:<>
       [:li
        [:button
         {:type "button"
          :class "nosco-richtext-ai-menu__button"
          :on-click
          (fn []
            (replace-selection-with-markdown! editor-api (::response state))
            (close-overlay! state dispatch editor-api))}
         [icons2/Icon {:icon icons2/Done-small}]
         (t/t "Replace selection")]]
       [:li
        [:button
         {:type "button"
          :class "nosco-richtext-ai-menu__button"
          :on-click
          (fn []
            (insert-response-below! state dispatch editor-api)
            (close-overlay! state dispatch editor-api))}
         [icons2/Icon {:icon icons2/Wrap-text-small}]
         (t/t "Insert below")]]
       [:li
        [:button
         {:type "button"
          :class "nosco-richtext-ai-menu__button"
          :on-click
          (fn []
            (copy-response-to-clipboard! state dispatch editor-api)
            (close-overlay! state dispatch editor-api))}
         [icons2/Icon {:icon icons2/Document-copy-small}]
         (t/t "Copy to clipboard")]]
       [:li
        [:button
         {:type "button"
          :class "nosco-richtext-ai-menu__button"
          :on-click #(submit-follow-up-edit-prompt! state dispatch editor-api continue-writing-prompt)}
         [icons2/Icon {:icon icons2/Create-small}]
         (t/t "Continue writing")]]])]

   [:ul {:class "nosco-richtext-ai-menu__section"}
    [:li
     [:button
      {:type "button"
       :class "nosco-richtext-ai-menu__button"
       :on-click
       (fn []
         (case (::mode state)
           ::create-mode (submit-initial-create-prompt! state dispatch editor-api (::initial-prompt state))
           ::edit-mode (submit-initial-edit-prompt! state dispatch editor-api (::initial-prompt state))))}
      [icons2/Icon {:icon icons2/Return-small}]
      (t/t "Try again")]]
    [:li
     [:button
      {:type "button"
       :class "nosco-richtext-ai-menu__button"
       :on-click
       (fn []
         (when (= ::create-mode (::mode state))
           (richtext-api/dispatch editor-api (pm/delete-selection-tr (richtext-api/get-state editor-api))))
         (close-overlay! state dispatch editor-api))}
      [icons2/Icon {:icon icons2/Delete-small}]
      (t/t "Discard")]]]])

(defnc PromptMenu [{:keys [state dispatch editor-api mode]} ref]
  {:wrap [(React/forwardRef)]}
  (let [{::keys [field]} state
        field-actions (cond->> (:aiActions field)
                        (= ::create-mode mode)
                        (filter :generative))
        global-actions (when-not (:hideGlobalAIActions (:settings field))
                         (cond->> (controllers/get-ai-actions)
                           (= ::create-mode mode)
                           (filter :generative)))]
    (when (or (seq field-actions) (seq global-actions))
      [:div {:class "nosco-richtext-ai-menu"
             :ref ref}
       (for [actions [field-actions global-actions]
             :when (seq actions)]
         [:ul {:class "nosco-richtext-ai-menu__section"}
          (for [action actions]
            [:li {:key (:id action)}
             [:button
              {:type "button"
               :class "nosco-richtext-ai-menu__button"
               :on-click (if (= ::create-mode mode)
                           #(submit-initial-create-prompt! state dispatch editor-api (:prompt action))
                           #(submit-initial-edit-prompt! state dispatch editor-api (:prompt action)))}
              [icons2/Icon {:icon (or (:icon action) icons2/AI-small)}]
              (:name action)]])])])))

(defnc AnimatedEllipsis []
  [:span {:class "nosco-richtext-ai-input__loading-icon"
          :dangerouslySetInnerHTML #js {:__html (shadow-resource/inline "./ellipsis-animated.svg")}}])

(def AIOverlayContainerContext (React/createContext nil))
(def ^:private AIOverlayContainerContextInner (React/createContext nil))

(defn convert-top-action [convert-top]
  {::type ::update-convert-top
   ::convert-top convert-top})

(defn use-convert-top
  "Return a function that will convert the viewport PM top coordinate
   a values that can be used to position the prompt to the AISuggestionWrapper"
  []
  (let [ai-scrolling-context (hooks/useContext AIOverlayContainerContext)]
    (hooks/useCallback (fn [top]
                         (let [container-rect (.getBoundingClientRect ai-scrolling-context)
                               container-top (.-top container-rect)
                               scroll-top (.-scrollTop ai-scrolling-context)]
                           (+ scroll-top (- top container-top))))
                       [ai-scrolling-context])))

(defnc AISuggestionsWrapper [{:keys [children]}]
  (let [ai-scrolling-context (hooks/useContext AIOverlayContainerContext)
        overlay-context (hooks/useContext popup/OverlayContainerContext)
        value (or ai-scrolling-context overlay-context js/document.body)]
    [:provider {:context AIOverlayContainerContextInner
                :value value}
     children]))

(defnc AISuggestions
  [{:keys [state
           dispatch
           editor-api
           ai-toolbar-button-ref]}]
  (let [{::keys [open position loading streaming-response response]} state
        portal-container-ref (hooks/useContext AIOverlayContainerContextInner)
        Portal (.-Portal (usePortal #js {:bindTo portal-container-ref}))
        overlay-ref (React/useRef)
        menu-ref (React/useRef)
        on-close (hooks/useCallback (fn [] (dispatch (close-ai-suggestions-action))) [dispatch])]
    (popup/use-click-outside open on-close overlay-ref menu-ref ai-toolbar-button-ref)
    (popup/use-escape open on-close)
    (when open
      [Portal
       [:div
        {:class ["nosco-richtext-ai-container"
                 (when (string/blank? (::text state)) "nosco-richtext-ai-container--no-selected-text")]
         :style {:--offset-top (:top position)
                 :--offset-left (:left position)}}
        [:div
         {:class "nosco-richtext-ai-overlay-outer"
          :style {:width (:width position)}}
         [:div {:ref overlay-ref :class "nosco-richtext-ai-outer"}
          (when (and (not= ::create-mode (::mode state)) (or streaming-response response))
            [:div {:class "nosco-richtext-ai-suggestion"}
             [:div
              {:class "ProseMirror"
               :style {:white-space "initial"}
               :dangerouslySetInnerHTML #js {:__html (richtext-markdown/markdown->html (or streaming-response response))}}]])
          [:div {:class "nosco-richtext-ai-input"}
           [icons2/Icon
            {:icon icons2/AI-large
             :color colors/info-600}]
           (cond
             loading
             [:div {:class "nosco-richtext-ai-input__loading"}
              (t/t "Nosco AI is writing")
              [AnimatedEllipsis]]

             :else
             [:form
              {:class "nosco-richtext-ai-input__input-outer"
               :on-submit
               (fn [^js event]
                 (let [user-prompt (-> (.. event -target -elements)
                                       (gobj/get "user-prompt")
                                       (.. -value))]
                   (.preventDefault event)
                   (.stopPropagation event)
                   (case (::mode state)
                     ::create-mode
                     (if (::initial-prompt state)
                       (submit-follow-up-create-prompt! state dispatch editor-api user-prompt)
                       (submit-initial-create-prompt! state dispatch editor-api user-prompt))

                     ::edit-mode
                     (if (::initial-prompt state)
                       (submit-follow-up-edit-prompt! state dispatch editor-api user-prompt)
                       (submit-initial-edit-prompt! state dispatch editor-api user-prompt)))))}
              [:input
               {:type "text"
                :name "user-prompt"
                :placeholder (if response
                               (t/t "Tell Nosco AI what to do next...")
                               (t/t "Ask Nosco AI to write anything..."))
                :class "nosco-richtext-ai-input__input"
                :auto-focus true
                :auto-complete "off"}]])
           [pills/Pill
            {:color :info
             :size :condensed
             :label (t/t "Beta")}]]]

         (cond
           loading
           nil

           response
           [ResponseMenu
            {:ref menu-ref
             :state state
             :dispatch dispatch
             :editor-api editor-api}]

           :else
           [PromptMenu
            {:ref menu-ref
             :state state
             :dispatch dispatch
             :editor-api editor-api
             :mode (::mode state)}])]]])))

(defn make-persistent-selection-decoration-set
  "Returns a DecorationSet wrapping the current selection."
  [state]
  (let [{:keys [selection doc]} (bean/bean state)
        {:keys [from to]} (bean/bean selection)
        decoration (.. pm-view/Decoration (inline from to #js {:class "ProseMirror-persistent-selection"}))]
    (.. pm-view/DecorationSet (create doc #js [decoration]))))

(def persistent-selection-plugin
  "Applies an inline decoration to the range of the selection, so that it can be styled even when the editor is not focused."
  (pm-state/Plugin.
   (bean/->js
    {:state
     {:init
      (fn []
        {::decorations (.. pm-view/DecorationSet -empty)})

      :apply
      (fn [_tr plugin-state _old-editor-state new-editor-state]
        (assoc plugin-state ::decorations (make-persistent-selection-decoration-set new-editor-state)))}

     :props
     {:decorations
      (fn [editor-state]
        (this-as this
          (::decorations (.. this (getState editor-state)))))}})))
